import playIcon from '../../assets/icon-play.svg'
import pauseIcon from '../../assets/icon-pause.svg'
import { IBidResponse } from '../../typings/IPrebid'
import { AdPlayer, IPlayerConfig } from '../adPlayer/AdPlayer'
import { DEFAULT_REFRESH_INTERVAL } from '../constants'
import { PlayerState } from '../enums/PlayerState'
import EventManager from '../eventManager/EventManager'
import GoogletagFacade from '../googletagFacade/GoogletagFacade'
import { addClass, removeClass } from '../helpers/DOM'
import { Flooring } from '../flooring/Flooring'
import { doOnce } from '../helpers/doOnce'
import Logger from '../helpers/Logger'
import type { Slot } from '../slots/Slot'
import { getOutstreamPlayerSize } from './hasOutstream'
import { OUTSTREAM_ACTIVE_CLASS } from '../styles/base'

/**
 * We don't want to refresh the slot before the video ad finishes
 * so we bump up the refreshTime a bit.
 * */

export const OUTSTREAM_REFRESH_TIME = 31

export class OutstreamRenderer {
  target: HTMLElement
  player: AdPlayer
  container: HTMLElement
  playPauseButton: HTMLElement
  bidResponse?: IBidResponse
  refreshCycle: Number

  static new(slot: Slot): OutstreamRenderer {
    switch (slot.type) {
      case 'Adhesion':
        return new AdhesionOutstreamRenderer(slot)
      default:
        return new OutstreamRenderer(slot)
    }
  }

  constructor(protected readonly slot: Slot) {
    this.slot = slot
  }

  async loadAd(bidResponse: IBidResponse) {
    Logger.debug('Outstream bid won:', bidResponse)
    this.slot.refreshTime = OUTSTREAM_REFRESH_TIME
    this.bidResponse = bidResponse

    if (!this.player) {
      const { ImaPlayer } = await import(
        /* webpackChunkName: "afterScroll"*/ '../adPlayer/ImaPlayer'
      )
      const adUnitEl = this.slot.wrapper.querySelector(
        `#${this.slot.id}`
      ) as HTMLElement
      this.container = initOutstreamContainer(adUnitEl)
      const playerConfig = getPlayerConfig(this.slot, this.container)
      this.player = await ImaPlayer.new(playerConfig)
      this.registerEventHandlers()
    }
    this.refreshCycle = this.slot.refreshes
    await this.player.initVideoAd(bidResponse)
    EventManager.trigger(EventManager.events.resetRefreshable, this.slot)
  }

  cleanup() {
    this.slot.refreshTime = DEFAULT_REFRESH_INTERVAL
  }

  protected registerEventHandlers({ player } = this) {
    const { events } = player
    player.onEvent(events.adError, this.onAdErrorHandler())
    player.onEvent(events.started, this.onAdStart())
    player.onEvent(events.adsCompleted, this.onAdsComplete())
    player.onEvent(events.visibilityChange, this.onVisibilityChange())
    player.onEvent(events.impression, this.onImpression())

    // Let the slot itself register events.
    this.slot.registerOutstreamEvents(player)
  }

  private onVisibilityChange =
    ({ player } = this) =>
    (isVisible: boolean) => {
      if (player.playerState === PlayerState.init) {
        return
      }
      isVisible ? player.play() : player.pause()
    }

  private onImpression =
    ({ slot } = this) =>
    () => {
      EventManager.trigger(EventManager.events.refreshSlot, slot)
    }

  /**
   * We only want to play the first second of an ad if it is off the screen
   * this.adPlayStatus will be 'resume' if the ad has been on screen
   */
  private onAdStart = doOnce(({ player } = this) => () => {
    setTimeout(() => {
      if (player.hasNotBeenOnScreen) {
        player.pause()
      }
    }, 1000)
  })

  private onAdErrorHandler =
    ({ bidResponse, player, slot } = this) =>
    /**
     * Less than ideal that we're coupled to ima here
     * RFF: Change NR Event to accept a generic error object so we can
     * construct our own error in the IMAPlayer.
     * */
    (adErrorEvent: google.ima.AdErrorEvent) => {
      const adError = adErrorEvent.getError()
      Logger.debug(adError)
      if (!bidResponse) {
        return
      }
      EventManager.trigger(EventManager.events.outstreamAdError, {
        adError,
        bidResponse,
        outstreamFloor: Flooring.getOutstreamFloor(slot.model)
      })
      player.cleanup()
      this.cleanup()
      GoogletagFacade.tryADifferentBidResponse(slot)
    }

  /** Refresh slot NOW if the ad has ended */
  private onAdsComplete =
    ({ slot, player } = this) =>
    () => {
      // since the renderer here does not know if some other trigger caused
      // the slot to refresh, we only want to short circuit if the slot has not
      // increased its refresh count since this ad started
      if (slot.refreshes === this.refreshCycle) {
        EventManager.trigger(EventManager.events.shortCircuitRefresh, slot)
      }
      player.cleanup()
      this.cleanup()
    }
}

export class AdhesionOutstreamRenderer extends OutstreamRenderer {
  constructor(protected slot: Slot) {
    super(slot)
  }

  cleanup() {
    super.cleanup()
    removeClass(document.body, OUTSTREAM_ACTIVE_CLASS)
    if (this.playPauseButton) {
      this.playPauseButton.style.display = 'none'
    }
  }
  async loadAd(bidResponse: IBidResponse) {
    await super.loadAd(bidResponse)
    addClass(document.body, OUTSTREAM_ACTIVE_CLASS)

    // If there is no adhesion close button, we need to attach a play/pause
    // button to adhesion outstream ads
    if (!this.slot.model.adhesion_close) {
      if (!this.playPauseButton) {
        this.playPauseButton = this.createPlayPauseButton(this.slot)
        this.container.insertAdjacentElement('beforeend', this.playPauseButton)

        // Setup listener to hide the playPauseButton when the ad is done.
        EventManager.on(EventManager.events.slotRefreshed, (slot) => {
          if (slot === this.slot) {
            this.cleanup()
          }
        })
      } else {
        this.playPauseButton.style.display = 'block'
      }
    }
  }

  createPlayPauseButton(slot: Slot) {
    const playPauseButton = document.createElement('div')
    playPauseButton.addEventListener('click', () => {
      const player = this.player
      if (player.playerState === PlayerState.pause) {
        player.play()
        playPauseButton.innerHTML = pauseHtml
        // If the user has plays a paused adhesion outstream video,
        // re-enable adhesion videos to appear
        slot.model.adhesion_video = false
      } else {
        player.pause()
        playPauseButton.innerHTML = playHtml
        // If the user has paused an adhesion outstream video,
        // disable further adhesion videos from appearing
        slot.model.adhesion_video = false
      }
    })

    const isPaused = this.player.playerState === PlayerState.pause
    playPauseButton.innerHTML = isPaused ? playHtml : pauseHtml
    playPauseButton.style.display = 'none'
    addClass(playPauseButton, 'mv-outstream-playPause')
    playPauseButton.style.display = 'block'
    playPauseButton.style.position = 'absolute'
    playPauseButton.style.top = '61px'
    playPauseButton.style.right = '-208px'

    return playPauseButton
  }
}

const initOutstreamContainer = (target: HTMLElement) => {
  // Create the ad display container.
  const outstreamContainer = document.createElement('div')
  outstreamContainer.className = 'mv-outstream-container'
  target.insertAdjacentElement('beforebegin', outstreamContainer)

  return outstreamContainer
}

const getPlayerConfig = (slot: Slot, container: HTMLElement): IPlayerConfig => {
  const [width, height] = getOutstreamPlayerSize(
    slot,
    slot.target?.parentElement
  )

  return {
    container,
    autoplay: true,
    muted: true,
    height,
    width
  }
}

const playHtml = `
    <span class='mv-adhesion-play-button' role='button' aria-label='Play Video Ad' >
      <img id="iconPlayAd" data-pin-nopin=true src=${playIcon} alt='' aria-label='Play Ad' width="23px" />
    </span>
  `

const pauseHtml = `
  <span class='mv-adhesion-pause-button' role='button' aria-label='Pause Video Ad' >
    <img id="iconPauseAd" data-pin-nopin=true src=${pauseIcon} alt='' aria-label='Pause Ad' width="23px" />
  </span>
`
